import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Router } from '@angular/router';
import { StorageService } from '../../../common/services/storage.service';
import { GetGrantsFilter, Grant } from '../../../common/models/grant';
import { CurrentUser } from '../../../common/models/user';
import { FormsModule } from '@angular/forms';
import { GrantService } from '../../../common/services/grant.service';
import { FileService } from '../../../common/services/file.service';
import { FileInfo } from '../../../common/models/file';
import { Subject, takeUntil } from 'rxjs';
import { GrantsFilterBuilder } from '../../../common/components/filter/builders/grants-filter-builder';
import { SearchService } from '../../../common/services/search.service';
import { FilterService } from '../../../common/components/filter/services/filter.service';


@Component({
    selector: 'app-grants',
    templateUrl: './grants.component.html',
    styleUrls: ['./grants.component.scss'],
    imports: [MatCardModule, MatButtonModule, MatCheckboxModule, CommonModule, FormsModule]
})
export class GrantsComponent implements OnInit {
  private destroy$ = new Subject<void>();

  user: CurrentUser | null = null;
  municipalityId: string = '';
  grants: Grant[] = [];
  filteredGrants: Grant[] = [];
  documentsMap: { [grantId: string]: FileInfo[] } = {};
  downloadedFiles: { [grantId: string]: { [link: string]: boolean } } = {};
  isAcknowledged: { [grantId: string]: boolean } = {};

  constructor(
    private storageService: StorageService,
    private filterBuilder: GrantsFilterBuilder,
    private grantService: GrantService,
    private searchService: SearchService,
    private filterService: FilterService,
    private fileService: FileService,
    private router: Router
  ) {}

  ngOnInit(): void { 
    this.searchService.currentSearchTerm$.pipe(takeUntil(this.destroy$)).subscribe(term => {
      this.applySearch(term);
     }); 
     this.filterService.filterChanges$.pipe(takeUntil(this.destroy$)).subscribe((filters) => {
       this.filterService.setFiltersInScope(filters);
       this.fetchGrants();
    });
      this.storageService.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
          this.user = user;
          this.municipalityId = this.user?.municipalityId ?? ''; 
          this.initializeFilters();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeFilters(): void {
    const startYear = 2025;
    const currentYear = new Date().getFullYear();

    const filterConfig =  this.filterBuilder 
      .reset()
      .years(startYear)
      .build();
  
      this.filterService.setFilterConfig(filterConfig);
      this.filterService.restoreFiltersInScope({ year: currentYear });
  }

  applySearch(searchTerm: string): void {
    searchTerm = searchTerm.trim().toLowerCase();
    if (searchTerm) {
      this.filteredGrants = this.grants.filter((grant: Grant) => {
        return (
          grant.name.toLowerCase().includes(searchTerm) ||
          grant.description.toLowerCase().includes(searchTerm) ||
          grant.year.toString().includes(searchTerm)
        );
      });
    } else {
      this.filteredGrants = [...this.grants];
    }
  }

  fetchGrants() {
    const savedFilterValues = this.filterService.getFiltersInScope();

    const filter = new GetGrantsFilter(this.municipalityId, savedFilterValues?.year);

    this.grantService.getGrants(filter).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: Grant[]) => {
        this.grants = data;
        this.filteredGrants = data;
        this.grants.forEach(grant => {
          this.fetchDocuments(grant.id);
        });
      },
      error: (error) => {
        console.error('Error fetching grants', error);
      }
    });
  }

  fetchDocuments(grantId: string): void {
    const storageLocation = `municipality/${this.user?.municipalityId}/grants/${grantId}`;
    this.fileService.listDocuments(storageLocation).pipe(takeUntil(this.destroy$)).subscribe({
      next: (documents: FileInfo[]) => {
        this.documentsMap[grantId] = documents;
        this.downloadedFiles[grantId] = {};
        documents.forEach(doc => this.downloadedFiles[grantId][doc.link] = false);
      },
      error: (error) => {
        console.error(`Error fetching documents for grant ${grantId}`, error);
      }
    });
  }

  onDocumentDownloaded(grantId: string, doc: FileInfo): void {
    // Call the document service to download the file
    this.fileService.downloadDocument(doc.link).pipe(takeUntil(this.destroy$)).subscribe({
      next: (blob: Blob) => {
        // Create a URL for the downloaded Blob and trigger download
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        // Mark the document as downloaded
        this.downloadedFiles[grantId][doc.link] = true;
        this.isAcknowledged[grantId] = this.areAllDocumentsDownloaded(grantId);
      },
      error: (error) => {
        console.error(`Error downloading document ${doc.name}`, error);
      }
    });
  }

  areAllDocumentsDownloaded(grantId: string): boolean {
    const documents = this.documentsMap[grantId] || [];
    return documents.every(d => this.downloadedFiles[grantId][d.link]);
  }

  acknowledgeDocuments(grantId: string, event: any): void {
    this.isAcknowledged[grantId] = event.checked;
  }

  applyForGrant(grant: Grant): void {
    if (this.isAcknowledged[grant.id]) {
      this.router.navigate(['/association/applications/', grant.id, 'create']);
    }
  }
}